import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useConfirmDialog } from '../contexts/ConfirmContext';
import TableListFooter from '../components/TableListFooter';
import {
  fetchAnnouncementGroups,
  createAnnouncementGroup,
  updateAnnouncementGroup,
  deleteAnnouncementGroup,
  fetchAdminAnnouncements,
  createAnnouncement,
  fetchAdminAnnouncementDetail,
  parseAdminAnnouncementsResult,
} from '../services/announcementsService';
import { searchUsers } from '../services/usersService';
import { includesNormalized } from '../utils/searchHelpers';
import DatePicker, { registerLocale } from 'react-datepicker';
import tr from 'date-fns/locale/tr';
import 'react-datepicker/dist/react-datepicker.css';
import './DuyurularPage.css';
import { getUserRoleLabel } from '../constants/userRoles';

registerLocale('tr', tr);

const getErrorMessage = (error, fallback) => {
  if (!error) return fallback;
  if (error?.status === 401) return 'Oturum süresi dolmuş olabilir; tekrar giriş yapın.';
  if (error?.status === 403) return 'Bu sayfa yalnızca yöneticiler içindir.';
  return error?.message || error?.data?.mesaj || fallback;
};

const formatDateTime = (value) => {
  if (!value) return '—';
  try {
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return '—';
    return d.toLocaleString('tr-TR', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return '—';
  }
};

const HEDEF_LABEL = {
  TUMU: 'Tümü (TS + teknisyen)',
  TEKNIK_SERVIS: 'Teknik servisler',
  TEKNISYEN: 'Teknisyenler',
  GRUPLAR: 'Özel gruplar',
};

/** Liste / detayda hedef sütunu; GRUPLAR iken seçili grup adları parantez içinde. */
const formatHedefWithGruplar = (hedefTip, grupIds, gruplarList) => {
  const base = HEDEF_LABEL[hedefTip] || hedefTip;
  if (hedefTip !== 'GRUPLAR') return base;
  const ids = Array.isArray(grupIds) ? grupIds : [];
  if (ids.length === 0) return `${base} (—)`;
  const adlar = ids.map((id) => gruplarList.find((g) => String(g.id) === String(id))?.ad).filter(Boolean);
  let ic;
  if (adlar.length === ids.length) {
    ic = adlar.join(', ');
  } else if (adlar.length > 0) {
    ic = `${adlar.join(', ')} (+${ids.length - adlar.length})`;
  } else {
    ic = '—';
  }
  return `${base} (${ic})`;
};

const uyeSatirAltMetin = (u) => {
  const parcalar = [];
  const adsoy = [u.ad, u.soyad].filter(Boolean).join(' ');
  if (adsoy) parcalar.push(adsoy);
  if (u.techService?.ad) parcalar.push(u.techService.ad);
  if (u.techService?.sehirAd) parcalar.push(u.techService.sehirAd);
  return parcalar.length ? parcalar.join(' · ') : null;
};

const pad2 = (n) => String(n).padStart(2, '0');

/** Yerel `datetime-local` dizesi (API'ye gönderirken yine Date ile ISO yapılıyor). */
const toDatetimeLocalValue = (date) => {
  if (!date || !(date instanceof Date) || Number.isNaN(date.getTime())) return '';
  return `${date.getFullYear()}-${pad2(date.getMonth() + 1)}-${pad2(date.getDate())}T${pad2(date.getHours())}:${pad2(
    date.getMinutes()
  )}`;
};

const parseDatetimeLocalToDate = (s) => {
  if (!s) return new Date();
  const d = new Date(s);
  return Number.isNaN(d.getTime()) ? new Date() : d;
};

const DuyurularPage = () => {
  const { confirm } = useConfirmDialog();
  const { user } = useAuth();

  const [sekme, setSekme] = useState('mesajlar');
  const [gruplar, setGruplar] = useState([]);
  const [duyurular, setDuyurular] = useState([]);
  const [sayfalama, setSayfalama] = useState({
    sayfa: 1,
    limit: 25,
    toplamSayfa: 1,
    toplamKayit: 0,
  });
  const [arsivFiltre, setArsivFiltre] = useState(false);
  const [yukleniyor, setYukleniyor] = useState(true);
  const [hata, setHata] = useState('');

  const [composeAcik, setComposeAcik] = useState(false);
  const [composeGonderiliyor, setComposeGonderiliyor] = useState(false);
  const [baslik, setBaslik] = useState('');
  const [icerik, setIcerik] = useState('');
  const [hedefTip, setHedefTip] = useState('TUMU');
  const [seciliGrupIds, setSeciliGrupIds] = useState([]);
  const [genelBitisMod, setGenelBitisMod] = useState('YAYINDAN_GUN');
  const [genelBitisTarihi, setGenelBitisTarihi] = useState('');
  const [genelYayinSonrasiGun, setGenelYayinSonrasiGun] = useState(30);
  const [okunmaSonrasiGun, setOkunmaSonrasiGun] = useState(3);

  const [duyuruDetayModal, setDuyuruDetayModal] = useState(null);

  const [grupModal, setGrupModal] = useState(null);
  const [yeniGrupAd, setYeniGrupAd] = useState('');
  const [grupUyeler, setGrupUyeler] = useState([]);
  const [arama, setArama] = useState('');
  const [aramaSonuc, setAramaSonuc] = useState([]);
  const [uyeAramaYukleniyor, setUyeAramaYukleniyor] = useState(false);
  const [grupSecimArama, setGrupSecimArama] = useState('');
  const [grupHizliSecim, setGrupHizliSecim] = useState('');
  const [bitisZamanModalAcik, setBitisZamanModalAcik] = useState(false);
  const [bitisZamanDraft, setBitisZamanDraft] = useState(() => new Date());

  const gruplarFiltreliMesaj = useMemo(() => {
    const q = grupSecimArama.trim();
    if (!q) return gruplar;
    return gruplar.filter((g) => includesNormalized(g.ad, q));
  }, [gruplar, grupSecimArama]);

  useEffect(() => {
    if (!composeAcik) {
      setBitisZamanModalAcik(false);
    }
  }, [composeAcik]);

  useEffect(() => {
    if (!grupModal) {
      setArama('');
      setAramaSonuc([]);
      setUyeAramaYukleniyor(false);
      return undefined;
    }
    const q = arama.trim();
    if (q.length < 2) {
      setAramaSonuc([]);
      setUyeAramaYukleniyor(false);
      return undefined;
    }
    setUyeAramaYukleniyor(true);
    const timer = setTimeout(async () => {
      try {
        const res = await searchUsers(q, { aktif: true, limit: 25, genis: true });
        const list = res?.veri?.kullanicilar || res?.veri?.users || [];
        const inIds = new Set(grupUyeler.map((x) => x.id));
        setAramaSonuc(
          list.filter((u) => {
            const id = String(u.id || u._id || '');
            if (!id || inIds.has(id)) return false;
            return u.rol === 'tech-service' || u.rol === 'tech-person';
          })
        );
      } catch {
        setAramaSonuc([]);
      } finally {
        setUyeAramaYukleniyor(false);
      }
    }, 400);
    return () => clearTimeout(timer);
  }, [arama, grupModal, grupUyeler]);

  const gruplariYukle = useCallback(async () => {
    try {
      const list = await fetchAnnouncementGroups();
      setGruplar(Array.isArray(list) ? list : []);
    } catch (e) {
      if (window.showNotification) {
        window.showNotification('error', getErrorMessage(e, 'Gruplar yüklenemedi'));
      }
    }
  }, []);

  const duyurulariYukle = useCallback(async () => {
    setYukleniyor(true);
    setHata('');
    try {
      const res = await fetchAdminAnnouncements({
        sayfa: sayfalama.sayfa,
        limit: 25,
        arsiv: arsivFiltre,
      });
      const { duyurular: liste, sayfalama: p } = parseAdminAnnouncementsResult(res);
      setDuyurular(liste);
      setSayfalama((prev) => ({
        ...prev,
        toplamSayfa: p.toplamSayfa || 1,
        toplamKayit: p.toplamKayit ?? 0,
      }));
    } catch (e) {
      setHata(getErrorMessage(e, 'Duyurular yüklenemedi'));
      setDuyurular([]);
    } finally {
      setYukleniyor(false);
    }
  }, [sayfalama.sayfa, arsivFiltre]);

  useEffect(() => {
    if (user?.rol !== 'admin') return;
    gruplariYukle();
  }, [user?.rol, gruplariYukle]);

  useEffect(() => {
    if (user?.rol !== 'admin' || sekme !== 'mesajlar') return;
    duyurulariYukle();
  }, [user?.rol, sekme, duyurulariYukle]);

  if (user?.rol !== 'admin') {
    return <Navigate to="/" replace />;
  }

  const composeAc = () => {
    setBaslik('');
    setIcerik('');
    setHedefTip('TUMU');
    setSeciliGrupIds([]);
    setGrupSecimArama('');
    setGrupHizliSecim('');
    setGenelBitisMod('YAYINDAN_GUN');
    setGenelBitisTarihi('');
    setGenelYayinSonrasiGun(30);
    setOkunmaSonrasiGun(3);
    setComposeAcik(true);
  };

  const composeGonder = async (e) => {
    e.preventDefault();
    if (!baslik.trim() || !icerik.trim()) {
      if (window.showNotification) window.showNotification('error', 'Başlık ve içerik gerekli');
      return;
    }
    if (hedefTip === 'GRUPLAR' && seciliGrupIds.length === 0) {
      if (window.showNotification) window.showNotification('error', 'En az bir grup seçin');
      return;
    }
    const body = {
      baslik: baslik.trim(),
      icerik: icerik.trim(),
      hedefTip,
      grupIds: hedefTip === 'GRUPLAR' ? seciliGrupIds : [],
      okunmaSonrasiGun: Number(okunmaSonrasiGun) || 1,
      genelBitisMod,
      genelBitisTarihi:
        genelBitisMod === 'TARIH' && genelBitisTarihi
          ? new Date(genelBitisTarihi).toISOString()
          : null,
      genelYayinSonrasiGun:
        genelBitisMod === 'YAYINDAN_GUN' ? Number(genelYayinSonrasiGun) || 30 : null,
    };
    setComposeGonderiliyor(true);
    try {
      await createAnnouncement(body);
      if (window.showNotification) window.showNotification('success', 'Duyuru yayınlandı');
      setComposeAcik(false);
      duyurulariYukle();
    } catch (err) {
      if (window.showNotification) {
        window.showNotification('error', getErrorMessage(err, 'Gönderilemedi'));
      }
    } finally {
      setComposeGonderiliyor(false);
    }
  };

  const satirDetay = async (id) => {
    setDuyuruDetayModal({ yukleniyor: true, veri: null });
    try {
      const res = await fetchAdminAnnouncementDetail(id);
      setDuyuruDetayModal({ yukleniyor: false, veri: res?.veri || null });
    } catch (e) {
      setDuyuruDetayModal(null);
      if (window.showNotification) {
        window.showNotification('error', getErrorMessage(e, 'Detay yüklenemedi'));
      }
    }
  };

  const grubaUyEkle = (u) => {
    const id = u.id || u._id;
    if (!id || grupUyeler.some((x) => x.id === id)) return;
    setGrupUyeler((prev) => [
      ...prev,
      {
        id: String(id),
        kullaniciAdi: u.kullaniciAdi,
        ad: u.ad,
        soyad: u.soyad,
        rol: u.rol,
      },
    ]);
  };

  const grupKaydet = async () => {
    const ad = grupModal === 'new' ? yeniGrupAd.trim() : (grupModal?.ad || '').trim();
    if (!ad) {
      if (window.showNotification) window.showNotification('error', 'Grup adı gerekli');
      return;
    }
    const uyeler = grupUyeler.map((u) => u.id);
    try {
      if (grupModal === 'new') {
        await createAnnouncementGroup({ ad, uyeler });
        if (window.showNotification) window.showNotification('success', 'Grup oluşturuldu');
      } else if (grupModal?.id) {
        await updateAnnouncementGroup(grupModal.id, {
          ad: grupModal.ad?.trim() || ad,
          uyeler,
        });
        if (window.showNotification) window.showNotification('success', 'Grup güncellendi');
      }
      setGrupModal(null);
      setYeniGrupAd('');
      setGrupUyeler([]);
      gruplariYukle();
    } catch (e) {
      if (window.showNotification) {
        window.showNotification('error', getErrorMessage(e, 'Kaydedilemedi'));
      }
    }
  };

  const grupSil = async (id) => {
    const ok = await confirm({
      title: 'Grubu sil',
      message: 'Bu grubu silmek istediğinize emin misiniz?',
      variant: 'danger',
      confirmLabel: 'Evet, sil',
    });
    if (!ok) return;
    try {
      await deleteAnnouncementGroup(id);
      if (window.showNotification) window.showNotification('success', 'Grup silindi');
      gruplariYukle();
    } catch (e) {
      if (window.showNotification) {
        window.showNotification('error', getErrorMessage(e, 'Silinemedi'));
      }
    }
  };

  const grupDuzenleAc = (g) => {
    setGrupModal({ id: g.id, ad: g.ad });
    setYeniGrupAd('');
    setGrupUyeler(
      (g.uyeler || []).map((u) => ({
        id: u.id,
        kullaniciAdi: u.kullaniciAdi,
        ad: u.ad,
        soyad: u.soyad,
        rol: u.rol,
      }))
    );
  };

  return (
    <main className="main-content duyurular-admin">
      <div className="content-header">
        <h1>Duyurular</h1>
      </div>

      <div className="tabs">
        <button
          type="button"
          className={`tab-btn ${sekme === 'mesajlar' ? 'active' : ''}`}
          onClick={() => setSekme('mesajlar')}
        >
          Mesajlar
        </button>
        <button
          type="button"
          className={`tab-btn ${sekme === 'gruplar' ? 'active' : ''}`}
          onClick={() => setSekme('gruplar')}
        >
          Gruplar
        </button>
      </div>

      {sekme === 'mesajlar' && (
        <>
          <div className="toolbar">
            <label
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '8px',
                cursor: 'pointer',
                fontSize: '14px',
                color: 'var(--text-dark)',
              }}
            >
              <input
                type="checkbox"
                checked={arsivFiltre}
                onChange={(e) => {
                  setArsivFiltre(e.target.checked);
                  setSayfalama((p) => ({ ...p, sayfa: 1 }));
                }}
              />
              <span>Sadece arşiv</span>
            </label>
            <button type="button" className="primary-btn add-user-btn" onClick={composeAc} title="Yeni duyuru">
              <i className="fas fa-pen" aria-hidden />
              <span>Mesaj yaz</span>
            </button>
          </div>

          <div className="card">
            {hata && (
              <div className="error-state" style={{ margin: '16px' }}>
                {hata}
              </div>
            )}
            {yukleniyor ? (
              <div className="loading-state">
                <div className="loading-spinner" />
                <div className="loading-text">Duyurular yükleniyor</div>
              </div>
            ) : !hata && duyurular.length === 0 ? (
              <div className="empty-state" style={{ padding: '40px', textAlign: 'center' }}>
                <i
                  className="fas fa-inbox"
                  style={{ fontSize: '32px', color: 'var(--text-light)', marginBottom: '12px', display: 'block' }}
                  aria-hidden
                />
                <div style={{ color: 'var(--text-gray)', fontSize: '14px' }}>
                  Herhangi bir mesaj bulunmamaktadır.
                </div>
              </div>
            ) : hata && duyurular.length === 0 ? null : (
              <div className="table-container">
                <table className="admins-table">
                  <thead>
                    <tr>
                      <th style={{ width: 48 }} aria-label="Okuma durumu" />
                      <th>Başlık</th>
                      <th>Yayın</th>
                      <th>Hedef</th>
                      <th>Okuma</th>
                      <th>Arşiv</th>
                    </tr>
                  </thead>
                  <tbody>
                    {duyurular.map((d) => (
                      <tr
                        key={d.id}
                        className="duyuru-row-click"
                        onClick={() => satirDetay(d.id)}
                        style={{ cursor: 'pointer' }}
                      >
                        <td>
                          <span
                            className={`duyuru-dot ${d.tamOkundu ? 'duyuru-dot--read' : 'duyuru-dot--unread'}`}
                            title={
                              d.tamOkundu
                                ? 'Tüm hedef kitle okudu'
                                : 'Okumayan alıcılar var'
                            }
                          />
                        </td>
                        <td style={{ fontWeight: 600 }}>{d.baslik}</td>
                        <td>{formatDateTime(d.yayinAt)}</td>
                        <td>{formatHedefWithGruplar(d.hedefTip, d.grupIds, gruplar)}</td>
                        <td>
                          {d.okuyanSayisi ?? 0} / {d.kitleBuyuklugu ?? '—'}
                        </td>
                        <td>{d.arsivlendiAt ? formatDateTime(d.arsivlendiAt) : '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {!yukleniyor && (
            <TableListFooter
              totalRecords={sayfalama.toplamKayit ?? 0}
              currentPage={sayfalama.sayfa}
              totalPages={sayfalama.toplamSayfa || 1}
              onPageChange={(p) => setSayfalama((prev) => ({ ...prev, sayfa: p }))}
              loading={yukleniyor}
            />
          )}
        </>
      )}

      {sekme === 'gruplar' && (
        <>
          <div className="toolbar" style={{ justifyContent: 'flex-end' }}>
            <button
              type="button"
              className="primary-btn add-user-btn"
              onClick={() => {
                setGrupModal('new');
                setYeniGrupAd('');
                setGrupUyeler([]);
              }}
            >
              <i className="fas fa-plus" aria-hidden />
              <span>Yeni grup</span>
            </button>
          </div>
          <div className="card">
            {gruplar.length === 0 ? (
              <div className="empty-state" style={{ padding: '40px', textAlign: 'center' }}>
                <i
                  className="fas fa-users"
                  style={{ fontSize: '32px', color: 'var(--text-light)', marginBottom: '12px', display: 'block' }}
                  aria-hidden
                />
                <div style={{ color: 'var(--text-gray)', fontSize: '14px' }}>Henüz tanımlı grup yok.</div>
              </div>
            ) : (
              <div className="table-container">
                <table className="admins-table">
                  <thead>
                    <tr>
                      <th>Grup adı</th>
                      <th>Üye sayısı</th>
                      <th className="islemler-sutunu">İşlemler</th>
                    </tr>
                  </thead>
                  <tbody>
                    {gruplar.map((g) => (
                      <tr key={g.id}>
                        <td style={{ fontWeight: 600 }}>{g.ad}</td>
                        <td>{g.uyeler?.length || 0}</td>
                        <td className="table-actions islemler-sutunu">
                          <button
                            type="button"
                            className="category-action-btn edit-btn"
                            onClick={() => grupDuzenleAc(g)}
                            title="Düzenle"
                          >
                            <i className="fas fa-edit" />
                          </button>
                          <button
                            type="button"
                            className="category-action-btn delete-btn"
                            onClick={() => grupSil(g.id)}
                            title="Sil"
                          >
                            <i className="fas fa-trash" />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </>
      )}

        {composeAcik && (
          <div className="duyuru-modal-backdrop" role="presentation" onClick={() => setComposeAcik(false)}>
            <div
              className="duyuru-modal-shell duyuru-modal-shell--compose"
              role="dialog"
              aria-labelledby="compose-title"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="duyuru-modal-header">
                <div>
                  <h2 id="compose-title" className="duyuru-modal-title">
                    Yeni duyuru
                  </h2>
                  <p className="duyuru-modal-sub">Başlık, içerik ve hedef kitleyi ayarlayın; özel gruplarda çoklu seçim yapabilirsiniz.</p>
                </div>
                <button
                  type="button"
                  className="duyuru-modal-close"
                  aria-label="Kapat"
                  onClick={() => {
                    setBitisZamanModalAcik(false);
                    setComposeAcik(false);
                  }}
                >
                  <i className="fas fa-times" />
                </button>
              </div>
              <div className="duyuru-modal-body">
                <form id="duyuru-compose-form" onSubmit={composeGonder} className="duyuru-compose-form">
                  <div className="duyuru-field-grid">
                    <label className="duyuru-floating-label">
                      <span>Başlık</span>
                      <input value={baslik} onChange={(e) => setBaslik(e.target.value)} required placeholder="Kısa ve net bir başlık" />
                    </label>
                    <label className="duyuru-floating-label duyuru-field-span2">
                      <span>İçerik</span>
                      <textarea value={icerik} onChange={(e) => setIcerik(e.target.value)} rows={5} required placeholder="Mesaj metni" />
                    </label>
                  </div>
                  <div className="duyuru-section">
                    <span className="duyuru-section-label">Hedef kitle</span>
                    <div className="duyuru-hedef-grid">
                      {['TUMU', 'TEKNIK_SERVIS', 'TEKNISYEN', 'GRUPLAR'].map((v) => (
                        <label key={v} className={`duyuru-hedef-card ${hedefTip === v ? 'duyuru-hedef-card--active' : ''}`}>
                          <input
                            type="radio"
                            name="hedef"
                            checked={hedefTip === v}
                            onChange={() => {
                              setHedefTip(v);
                              if (v !== 'GRUPLAR') {
                                setSeciliGrupIds([]);
                                setGrupSecimArama('');
                                setGrupHizliSecim('');
                              }
                            }}
                          />
                          <span className="duyuru-hedef-card-text">{HEDEF_LABEL[v]}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                  {hedefTip === 'GRUPLAR' && (
                    <div className="duyuru-section duyuru-grup-picker">
                      <span className="duyuru-section-label">Özel gruplar</span>
                      <p className="duyuru-hint">
                        Grup adına göre arayın; istediğiniz kadar grubu işaretleyin veya açılır listeden tek tıkla ekleyin.
                      </p>
                      <div className="duyuru-grup-picker-row">
                        <div className="duyuru-search-input-wrap">
                          <i className="fas fa-search duyuru-search-input-icon" aria-hidden />
                          <input
                            type="text"
                            className="duyuru-search-input duyuru-search-input--grup-ara"
                            value={grupSecimArama}
                            onChange={(e) => setGrupSecimArama(e.target.value)}
                            placeholder="Grup adında ara…"
                            autoComplete="off"
                            enterKeyHint="search"
                          />
                        </div>
                        <select
                          className="duyuru-grup-select-native"
                          value={grupHizliSecim}
                          onChange={(e) => {
                            const id = e.target.value;
                            if (id) {
                              setSeciliGrupIds((s) => (s.includes(id) ? s : [...s, id]));
                            }
                            setGrupHizliSecim('');
                          }}
                          aria-label="Listeden grup ekle"
                        >
                          <option value="">Listeden grup ekle…</option>
                          {gruplarFiltreliMesaj.map((g) => (
                            <option key={g.id} value={g.id} disabled={seciliGrupIds.includes(g.id)}>
                              {g.ad}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div className="duyuru-grup-multilist" role="group" aria-label="Grup seçimi">
                        {gruplarFiltreliMesaj.length === 0 ? (
                          <p className="duyuru-muted duyuru-grup-empty">Bu aramayla eşleşen grup yok.</p>
                        ) : (
                          gruplarFiltreliMesaj.map((g) => (
                            <label key={g.id} className="duyuru-grup-check-row">
                              <input
                                type="checkbox"
                                checked={seciliGrupIds.includes(g.id)}
                                onChange={(e) => {
                                  if (e.target.checked) {
                                    setSeciliGrupIds((s) => [...s, g.id]);
                                  } else {
                                    setSeciliGrupIds((s) => s.filter((x) => x !== g.id));
                                  }
                                }}
                              />
                              <span className="duyuru-grup-check-name">{g.ad}</span>
                            </label>
                          ))
                        )}
                      </div>
                      {seciliGrupIds.length > 0 && (
                        <div className="duyuru-grup-chips-bar" aria-live="polite">
                          {seciliGrupIds.map((gid) => {
                            const ad = gruplar.find((x) => x.id === gid)?.ad || gid;
                            return (
                              <span key={gid} className="duyuru-grup-chip">
                                {ad}
                                <button
                                  type="button"
                                  className="duyuru-grup-chip-x"
                                  aria-label={`${ad} seçimini kaldır`}
                                  onClick={() => setSeciliGrupIds((s) => s.filter((x) => x !== gid))}
                                >
                                  ×
                                </button>
                              </span>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  )}
                  <div className="duyuru-section">
                    <span className="duyuru-section-label">Genel geçerlilik (mesajın toplam ömrü)</span>
                    <div className="duyuru-bitis-grid">
                      <label className={`duyuru-mini-choice ${genelBitisMod === 'YOK' ? 'duyuru-mini-choice--on' : ''}`}>
                        <input type="radio" name="gbit" checked={genelBitisMod === 'YOK'} onChange={() => setGenelBitisMod('YOK')} />
                        Sınırsız
                      </label>
                      <label className={`duyuru-mini-choice ${genelBitisMod === 'TARIH' ? 'duyuru-mini-choice--on' : ''}`}>
                        <input type="radio" name="gbit" checked={genelBitisMod === 'TARIH'} onChange={() => setGenelBitisMod('TARIH')} />
                        Tarih / saat
                      </label>
                      <label className={`duyuru-mini-choice ${genelBitisMod === 'YAYINDAN_GUN' ? 'duyuru-mini-choice--on' : ''}`}>
                        <input
                          type="radio"
                          name="gbit"
                          checked={genelBitisMod === 'YAYINDAN_GUN'}
                          onChange={() => setGenelBitisMod('YAYINDAN_GUN')}
                        />
                        Yayın + gün
                      </label>
                    </div>
                    {genelBitisMod === 'TARIH' && (
                      <div className="duyuru-bitis-trigger-wrap">
                        <button
                          type="button"
                          className="duyuru-bitis-trigger"
                          onClick={() => {
                            setBitisZamanDraft(parseDatetimeLocalToDate(genelBitisTarihi));
                            setBitisZamanModalAcik(true);
                          }}
                        >
                          <span className="duyuru-bitis-trigger-icon" aria-hidden>
                            <i className="fas fa-calendar-alt" />
                          </span>
                          <span className="duyuru-bitis-trigger-label">
                            {genelBitisTarihi
                              ? formatDateTime(genelBitisTarihi)
                              : 'Tarih ve saat seçin'}
                          </span>
                          <i className="fas fa-chevron-right duyuru-bitis-trigger-chev" aria-hidden />
                        </button>
                      </div>
                    )}
                    {genelBitisMod === 'YAYINDAN_GUN' && (
                      <label className="duyuru-inline-num">
                        <span>Gün</span>
                        <input
                          type="number"
                          min={1}
                          max={3650}
                          value={genelYayinSonrasiGun}
                          onChange={(e) => setGenelYayinSonrasiGun(Number(e.target.value))}
                        />
                      </label>
                    )}
                  </div>
                  <label className="duyuru-inline-num">
                    <span>Okunduktan kaç gün sonra gizlensin</span>
                    <input
                      type="number"
                      min={1}
                      max={3650}
                      value={okunmaSonrasiGun}
                      onChange={(e) => setOkunmaSonrasiGun(Number(e.target.value))}
                    />
                  </label>
                </form>
              </div>
              <div className="duyuru-modal-footer">
                <button
                  type="button"
                  className="btn-ghost"
                  onClick={() => {
                    setBitisZamanModalAcik(false);
                    setComposeAcik(false);
                  }}
                >
                  Vazgeç
                </button>
                <button type="submit" form="duyuru-compose-form" className="btn-primary" disabled={composeGonderiliyor}>
                  {composeGonderiliyor ? 'Gönderiliyor…' : 'Yayınla'}
                </button>
              </div>

              {bitisZamanModalAcik && (
                <div
                  className="duyuru-modal-nested-backdrop"
                  role="presentation"
                  onClick={() => setBitisZamanModalAcik(false)}
                >
                  <div
                    className="duyuru-modal-shell duyuru-modal-shell--bitis-pick"
                    role="dialog"
                    aria-labelledby="bitis-zaman-baslik"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <div className="duyuru-modal-header duyuru-modal-header--compact">
                      <div>
                        <h2 id="bitis-zaman-baslik" className="duyuru-modal-title">
                          Bitiş tarihi ve saati
                        </h2>
                        <p className="duyuru-modal-sub">Takvimden günü, sağdan saati seçin; uygula ile onaylayın.</p>
                      </div>
                      <button
                        type="button"
                        className="duyuru-modal-close"
                        aria-label="Kapat"
                        onClick={() => setBitisZamanModalAcik(false)}
                      >
                        <i className="fas fa-times" />
                      </button>
                    </div>
                    <div className="duyuru-modal-body duyuru-modal-body--datepicker">
                      <div className="duyuru-datepicker-inline-wrap">
                        <DatePicker
                          selected={bitisZamanDraft}
                          onChange={(d) => d && setBitisZamanDraft(d)}
                          locale="tr"
                          showTimeSelect
                          timeIntervals={15}
                          timeCaption="Saat"
                          dateFormat="dd.MM.yyyy"
                          inline
                          calendarClassName="duyuru-rdp-calendar"
                        />
                      </div>
                    </div>
                    <div className="duyuru-modal-footer">
                      <button type="button" className="btn-ghost" onClick={() => setBitisZamanModalAcik(false)}>
                        İptal
                      </button>
                      <button
                        type="button"
                        className="btn-primary"
                        onClick={() => {
                          setGenelBitisTarihi(toDatetimeLocalValue(bitisZamanDraft));
                          setBitisZamanModalAcik(false);
                        }}
                      >
                        Uygula
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {duyuruDetayModal && (
          <div
            className="duyuru-modal-backdrop"
            role="presentation"
            onClick={() => setDuyuruDetayModal(null)}
          >
            <div className="duyuru-modal-shell duyuru-modal-shell--wide" onClick={(e) => e.stopPropagation()}>
              <div className="duyuru-modal-header">
                <div>
                  <h2 className="duyuru-modal-title">
                    {duyuruDetayModal.yukleniyor || !duyuruDetayModal.veri
                      ? 'Duyuru detayı'
                      : duyuruDetayModal.veri.duyuru?.baslik}
                  </h2>
                  {!duyuruDetayModal.yukleniyor && duyuruDetayModal.veri && (
                    <p className="duyuru-modal-sub">
                      {formatDateTime(duyuruDetayModal.veri.duyuru?.yayinAt)} ·{' '}
                      {formatHedefWithGruplar(
                        duyuruDetayModal.veri.duyuru?.hedefTip,
                        duyuruDetayModal.veri.duyuru?.grupIds,
                        gruplar,
                      )}
                    </p>
                  )}
                </div>
                <button
                  type="button"
                  className="duyuru-modal-close"
                  aria-label="Kapat"
                  onClick={() => setDuyuruDetayModal(null)}
                >
                  <i className="fas fa-times" />
                </button>
              </div>
              <div className="duyuru-modal-body">
                {duyuruDetayModal.yukleniyor || !duyuruDetayModal.veri ? (
                  <p className="duyuru-muted">Yükleniyor…</p>
                ) : (
                  <>
                    <div className="duyuru-detail-body">{duyuruDetayModal.veri.duyuru?.icerik}</div>
                    <h4 className="duyuru-readers-title">
                      Okuyanlar ({duyuruDetayModal.veri.okuyanlar?.length || 0})
                    </h4>
                    <ul className="duyuru-readers">
                      {(duyuruDetayModal.veri.okuyanlar || []).map((r) => (
                        <li key={r.kullaniciId}>
                          {r.kullaniciAdi} — {formatDateTime(r.okunduAt)}
                        </li>
                      ))}
                    </ul>
                  </>
                )}
              </div>
              <div className="duyuru-modal-footer">
                <button type="button" className="btn-primary" onClick={() => setDuyuruDetayModal(null)}>
                  Kapat
                </button>
              </div>
            </div>
          </div>
        )}

        {grupModal && (
          <div className="duyuru-modal-backdrop" role="presentation" onClick={() => setGrupModal(null)}>
            <div className="duyuru-modal-shell duyuru-modal-shell--xl" onClick={(e) => e.stopPropagation()}>
              <div className="duyuru-modal-header">
                <div>
                  <h2 className="duyuru-modal-title">{grupModal === 'new' ? 'Yeni alıcı grubu' : 'Grup düzenle'}</h2>
                  <p className="duyuru-modal-sub">
                    İsim veya konum ile arayın; sonuçlardan üyeleri tek tıkla ekleyin. Kaydet ile kaydedilir.
                  </p>
                </div>
                <button type="button" className="duyuru-modal-close" aria-label="Kapat" onClick={() => setGrupModal(null)}>
                  <i className="fas fa-times" />
                </button>
              </div>
              <div className="duyuru-modal-body">
                <div className="duyuru-field-grid duyuru-field-grid--1">
                  {grupModal !== 'new' ? (
                    <label className="duyuru-floating-label">
                      <span>Grup adı</span>
                      <input
                        value={grupModal.ad}
                        onChange={(e) => setGrupModal({ ...grupModal, ad: e.target.value })}
                        placeholder="Grup adı"
                      />
                    </label>
                  ) : (
                    <label className="duyuru-floating-label">
                      <span>Grup adı</span>
                      <input
                        value={yeniGrupAd}
                        onChange={(e) => setYeniGrupAd(e.target.value)}
                        placeholder="Örn: Marmara servisleri"
                      />
                    </label>
                  )}
                </div>
                <div className="duyuru-section">
                  <span className="duyuru-section-label">Üye ara</span>
                  <p className="duyuru-hint">
                    En az 2 karakter; kullanıcı adı, ad, soyad, teknik servis adı / kodu, il veya ilçe adı ile aranır. Yazmayı
                    bıraktıktan kısa süre sonra sonuçlar gelir.
                  </p>
                  <div className="duyuru-search-input-wrap duyuru-search-input-wrap--lg">
                    <i className="fas fa-search duyuru-search-input-icon" aria-hidden />
                    <input
                      type="search"
                      className="duyuru-search-input"
                      value={arama}
                      onChange={(e) => setArama(e.target.value)}
                      placeholder="Örn: izmir, ahmet, ts adı, 345678…"
                      autoComplete="off"
                    />
                    {uyeAramaYukleniyor && (
                      <span className="duyuru-search-spinner" aria-live="polite">
                        <i className="fas fa-spinner fa-spin" />
                      </span>
                    )}
                  </div>
                  <div className="duyuru-uye-results">
                    {arama.trim().length > 0 && arama.trim().length < 2 ? (
                      <p className="duyuru-muted">Aramak için en az 2 karakter yazın.</p>
                    ) : null}
                    {arama.trim().length >= 2 && !uyeAramaYukleniyor && aramaSonuc.length === 0 ? (
                      <p className="duyuru-muted">Sonuç bulunamadı.</p>
                    ) : null}
                    {aramaSonuc.map((u) => {
                      const id = String(u.id || u._id);
                      const alt = uyeSatirAltMetin(u);
                      return (
                        <div key={id} className="duyuru-uye-hit">
                          <div className="duyuru-uye-hit-main">
                            <strong>{u.kullaniciAdi}</strong>
                            <span className="duyuru-uye-rol">{getUserRoleLabel(u.rol)}</span>
                            {alt ? <span className="duyuru-uye-sub">{alt}</span> : null}
                          </div>
                          <button type="button" className="duyuru-btn-ekle" onClick={() => grubaUyEkle(u)}>
                            Ekle
                          </button>
                        </div>
                      );
                    })}
                  </div>
                </div>
                <div className="duyuru-section">
                  <span className="duyuru-section-label">Gruptaki üyeler ({grupUyeler.length})</span>
                  <div className="duyuru-member-chips-grid">
                    {grupUyeler.length === 0 ? (
                      <p className="duyuru-muted">Henüz üye eklenmedi.</p>
                    ) : (
                      grupUyeler.map((u) => (
                        <span key={u.id} className="duyuru-member-chip-lg">
                          <span className="duyuru-member-chip-lg-text">
                            <strong>{u.kullaniciAdi}</strong>
                            <small>{getUserRoleLabel(u.rol)}</small>
                          </span>
                          <button
                            type="button"
                            className="duyuru-grup-chip-x"
                            aria-label="Üyeyi çıkar"
                            onClick={() => setGrupUyeler((prev) => prev.filter((x) => x.id !== u.id))}
                          >
                            ×
                          </button>
                        </span>
                      ))
                    )}
                  </div>
                </div>
              </div>
              <div className="duyuru-modal-footer">
                <button type="button" className="btn-ghost" onClick={() => setGrupModal(null)}>
                  Vazgeç
                </button>
                <button type="button" className="btn-primary" onClick={grupKaydet}>
                  Kaydet
                </button>
              </div>
            </div>
          </div>
        )}
    </main>
  );
};

export default DuyurularPage;
